import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  User,
  Bell,
  Palette,
  Shield,
  Wrench,
  Users,
  UserPlus,
  MoreVertical,
  Trash2,
  Pencil,
  LogOut,
  Check,
  X,
  Mail,
  Clock,
  RefreshCw,
  Globe,
} from "lucide-react";
import { useLanguageStore, SupportedLanguage } from "../../stores/language.store";
import { API_ENDPOINTS } from "../../config/api-endpoints";
import { apiFetch } from "../../services/api";
import { useSettingsStore } from "../../store/tenantStore";
import { useProperty } from "../../contexts/PropertyContext";
import { useAuthStore } from "../../store/authStore";
import { cn } from "../../lib/utils";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/Badge";
import { COLOR_PRESETS } from "../../config/themePresets";
import { toast } from "sonner";
import { applyColorPreset, applyDarkMode, applyFontSize } from "../../hooks/useThemeApplier";
import {
  getFamilyMembers,
  getPendingInvites,
  removeFamilyMember,
  cancelInvite,
  resendInvite,
  leaveHome,
  FamilyMember,
  FamilyInvite,
  SERVICE_TYPE_LABELS,
  ServiceType,
} from "../../services/family.api";
import { InviteSheet } from "./InviteSheet";
import { EditMemberSheet } from "./EditMemberSheet";

// ─── Nav sections ─────────────────────────────────────────────────────────────
type Section = "account" | "appearance" | "language" | "notifications" | "maintenance" | "security" | "household";

const NAV: { id: Section; label: string; icon: React.FC<{ className?: string }> }[] = [
  { id: "account",       label: "Account",       icon: User    },
  { id: "appearance",    label: "Appearance",    icon: Palette },
  { id: "language",      label: "Language",      icon: Globe   },
  { id: "notifications", label: "Notifications", icon: Bell    },
  { id: "maintenance",   label: "Maintenance",   icon: Wrench  },
  { id: "security",      label: "Security",      icon: Shield  },
  { id: "household",     label: "Household",     icon: Users   },
];

const SUPPORTED_LANGUAGES: { code: SupportedLanguage; label: string; flag: string; native: string }[] = [
  { code: "en", label: "English",      flag: "🇺🇸", native: "English"    },
  { code: "es", label: "Spanish",      flag: "🇪🇸", native: "Español"    },
  { code: "zh", label: "Chinese",      flag: "🇨🇳", native: "中文"         },
  { code: "hi", label: "Hindi",        flag: "🇮🇳", native: "हिंदी"       },
  { code: "fr", label: "French",       flag: "🇫🇷", native: "Français"   },
  { code: "pt", label: "Portuguese",   flag: "🇧🇷", native: "Português"  },
  { code: "ar", label: "Arabic",       flag: "🇸🇦", native: "العربية"    },
  { code: "ko", label: "Korean",       flag: "🇰🇷", native: "한국어"       },
  { code: "vi", label: "Vietnamese",   flag: "🇻🇳", native: "Tiếng Việt" },
  { code: "tl", label: "Filipino",     flag: "🇵🇭", native: "Filipino"   },
  { code: "de", label: "German",       flag: "🇩🇪", native: "Deutsch"    },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const ROLE_BADGE: Record<string, "default" | "secondary" | "success"> = {
  owner: "default",
  member: "success",
  viewer: "secondary",
};

function _formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function MemberAvatar({ name, url, size = 9 }: { name: string; url?: string; size?: number }) {
  const initials = name.split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase();
  if (url) {
    return <img src={url} alt={name} className={`h-${size} w-${size} rounded-full object-cover border shrink-0`} />;
  }
  return (
    <div className={`h-${size} w-${size} rounded-full bg-secondary border flex items-center justify-center text-xs font-medium shrink-0`}>
      {initials}
    </div>
  );
}

// ─── Member row ───────────────────────────────────────────────────────────────
function MemberRow({
  member, isMe, isOwner, onEdit, onRemove, onLeave,
}: {
  member: FamilyMember; isMe: boolean; isOwner: boolean;
  onEdit: () => void; onRemove: () => void; onLeave: () => void;
}) {
  const { t } = useTranslation('settings');
  const [menuOpen, setMenuOpen] = useState(false);
  const services = member.serviceAccess.map((s) => SERVICE_TYPE_LABELS[s.serviceType as ServiceType]).join(", ");

  return (
    <div className="flex items-center gap-3 py-3 group">
      <MemberAvatar name={member.user.name} url={member.user.avatarUrl} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium truncate">{member.user.name}</span>
          {isMe && <span className="text-[10px] border rounded-full px-1.5 py-0.5 text-muted-foreground leading-none">{t('household.you')}</span>}
          <Badge variant={ROLE_BADGE[member.role] ?? "secondary"} className="text-[10px]">{member.role}</Badge>
          {member.relation && <span className="text-[10px] text-muted-foreground">{member.relation}</span>}
        </div>
        <p className="text-xs text-muted-foreground truncate">{member.user.email}</p>
        <div className="flex gap-2 mt-0.5">
          {member.assetAccess.length > 0 && (
            <span className="text-[11px] text-muted-foreground">{member.assetAccess.length} asset{member.assetAccess.length !== 1 ? "s" : ""}</span>
          )}
          {member.grantFutureAssets && <span className="text-[11px] text-green-600 dark:text-green-400">{t('household.futureAssets')}</span>}
          {services && <span className="text-[11px] text-muted-foreground truncate">{services}</span>}
        </div>
      </div>

      {/* Actions */}
      {(isOwner || isMe) && (
        <div className="relative opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={() => setMenuOpen((o) => !o)}
            className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
          >
            <MoreVertical className="h-4 w-4" />
          </button>
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-20" onClick={() => setMenuOpen(false)} />
              <div className="absolute right-0 top-8 z-30 w-44 rounded-xl border bg-background shadow-xl py-1.5">
                {isMe ? (
                  <button
                    onClick={() => { setMenuOpen(false); onLeave(); }}
                    className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-destructive hover:bg-secondary/60 transition-colors"
                  >
                    <LogOut className="h-3.5 w-3.5" /> {t('household.leaveHome')}
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => { setMenuOpen(false); onEdit(); }}
                      className="flex items-center gap-2.5 w-full px-3 py-2 text-sm hover:bg-secondary/60 transition-colors"
                    >
                      <Pencil className="h-3.5 w-3.5" /> {t('household.editAccess')}
                    </button>
                    <div className="my-1 border-t" />
                    <button
                      onClick={() => { setMenuOpen(false); onRemove(); }}
                      className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-destructive hover:bg-secondary/60 transition-colors"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> {t('household.removeMember')}
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Pending invite row ───────────────────────────────────────────────────────
function InviteRow({
  invite, onCancel, onResend, busy,
}: {
  invite: FamilyInvite; onCancel: () => void; onResend: () => void; busy: boolean;
}) {
  const { t } = useTranslation('settings');
  const daysLeft = Math.max(
    0,
    Math.ceil((new Date(invite.expiresAt).getTime() - Date.now()) / 86_400_000)
  );
  return (
    <div className="flex items-center gap-3 py-3 group">
      <div className="h-9 w-9 rounded-full bg-secondary/60 border flex items-center justify-center shrink-0">
        <Mail className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{invite.email}</p>
        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
          <Badge variant={ROLE_BADGE[invite.role] ?? "secondary"} className="text-[10px]">{invite.role}</Badge>
          {invite.relation && <span className="text-[10px] text-muted-foreground">{invite.relation}</span>}
          <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
            <Clock className="h-3 w-3" />{t('household.daysLeft', { n: daysLeft })}
          </span>
        </div>
      </div>
      <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
        <button
          onClick={onResend} disabled={busy} title={t('email.resendTitle')}
          className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40"
        >
          <RefreshCw className="h-3.5 w-3.5" />
        </button>
        <button
          onClick={onCancel} disabled={busy} title={t('email.cancelTitle')}
          className="p-1.5 rounded-md hover:bg-secondary text-destructive transition-colors disabled:opacity-40"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

// ─── UserPreferences type ────────────────────────────────────────────────────
type UserPreferences = {
  emailNotifications: boolean;
  smsNotifications: boolean;
  pushNotifications: boolean;
  inAppNotifications: boolean;
  maintenanceReminders: boolean;
  serviceBookingNotifications: boolean;
  assetAlerts: boolean;
  marketingEmails: boolean;
  // maintenance
  autoScheduleMaintenance: boolean;
  advanceNoticeDays: number;
};

// Extracts only the boolean keys so togglePref is type-safe
type BoolPrefKey = { [K in keyof UserPreferences]: UserPreferences[K] extends boolean ? K : never }[keyof UserPreferences];

// ─── Reusable toggle row ──────────────────────────────────────────────────────
function NotifRow({ label, desc, value, saving, disabled, onToggle }: {
  label: string; desc: string; value: boolean;
  saving: boolean; disabled: boolean; onToggle: () => void;
}) {
  return (
    <div className="flex items-center justify-between px-4 py-4 gap-4">
      <div className="min-w-0">
        <p className="text-sm font-medium">{label}</p>
        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{desc}</p>
      </div>
      <button
        role="switch"
        aria-checked={value}
        disabled={saving || disabled}
        onClick={onToggle}
        className={cn(
          "relative inline-flex h-5 w-9 items-center rounded-full transition-colors shrink-0 disabled:opacity-40",
          value ? "bg-foreground" : "bg-secondary border border-border"
        )}
      >
        {saving ? (
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="h-2.5 w-2.5 border border-current border-t-transparent rounded-full animate-spin opacity-60" />
          </span>
        ) : (
          <span className={cn(
            "inline-block h-3.5 w-3.5 rounded-full bg-background transition-transform shadow-sm",
            value ? "translate-x-4" : "translate-x-0.5"
          )} />
        )}
      </button>
    </div>
  );
}

// ─── Language section ─────────────────────────────────────────────────────────
function LanguageSection() {
  const { t } = useTranslation('settings');
  const { language, setLanguage } = useLanguageStore();
  const [selected, setSelected] = React.useState<SupportedLanguage>(language);
  const [saved, setSaved] = React.useState(false);

  const handleSave = () => {
    setLanguage(selected);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-base font-medium">{t('region.language')}</h2>
        <p className="text-xs text-muted-foreground mt-0.5">{t('region.languageDesc', 'Choose the language used throughout the app.')}</p>
      </div>

      <div className="rounded-xl border divide-y overflow-hidden">
        {SUPPORTED_LANGUAGES.map((lang) => {
          const isSelected = selected === lang.code;
          return (
            <button
              key={lang.code}
              onClick={() => setSelected(lang.code)}
              className={cn(
                "w-full flex items-center gap-3 px-4 py-3.5 text-left transition-colors hover:bg-secondary/40",
                isSelected && "bg-foreground/5"
              )}
            >
              <span className="text-xl leading-none">{lang.flag}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{lang.label}</p>
                <p className="text-xs text-muted-foreground">{lang.native}</p>
              </div>
              {isSelected && <Check className="h-4 w-4 text-foreground shrink-0" />}
            </button>
          );
        })}
      </div>

      <button
        onClick={handleSave}
        disabled={selected === language}
        className={cn(
          "h-9 px-5 rounded-lg text-sm font-medium transition-all flex items-center gap-2",
          saved
            ? "bg-green-600 text-white"
            : "bg-foreground text-background hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
        )}
      >
        {saved ? (
          <><Check className="h-3.5 w-3.5" />{t('account.saved', 'Saved!')}</>
        ) : (
          t('region.saveLanguage', 'Save Language')
        )}
      </button>
    </div>
  );
}

// ─── Household section ────────────────────────────────────────────────────────
function HouseholdSection() {
  const { t } = useTranslation('settings');
  const { currentProperty } = useProperty();
  const { user } = useAuthStore();

  const [members, setMembers]         = useState<FamilyMember[]>([]);
  const [pending, setPending]         = useState<FamilyInvite[]>([]);
  const [loading, setLoading]         = useState(true);
  const [busy, setBusy]               = useState<string | null>(null);
  const [error, setError]             = useState("");
  const [showInvite, setShowInvite]   = useState(false);
  const [editing, setEditing]         = useState<FamilyMember | null>(null);

  const homeId = currentProperty?.id;

  // Determine owner status: property role or present as owner in members list
  const isOwner =
    currentProperty?.role === "owner" ||
    members.some((m) => m.userId === user?.id && m.role === "owner") ||
    // If no members loaded yet but the home API returned role=owner, treat as owner
    (!members.length && currentProperty?.role !== "member" && currentProperty?.role !== "viewer");

  const load = useCallback(async () => {
    if (!homeId) return;
    setLoading(true);
    setError("");
    try {
      const [membersData, pendingData] = await Promise.all([
        getFamilyMembers(homeId),
        isOwner ? getPendingInvites(homeId).catch(() => []) : Promise.resolve([]),
      ]);
      setMembers(membersData);
      setPending(pendingData);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to load household data");
    } finally {
      setLoading(false);
    }
  }, [homeId, isOwner]);

  useEffect(() => { load(); }, [load]);

  const handleRemove = async (m: FamilyMember) => {
    if (!window.confirm(t('household.confirmRemove', { name: m.user.name }))) return;
    setBusy(m.id);
    try { await removeFamilyMember(m.id); await load(); }
    catch (e: unknown) { alert(e instanceof Error ? e.message : "Failed"); }
    finally { setBusy(null); }
  };

  const handleLeave = async (m: FamilyMember) => {
    if (!window.confirm(t('household.confirmLeave'))) return;
    setBusy(m.id);
    try { await leaveHome(m.id); await load(); }
    catch (e: unknown) { alert(e instanceof Error ? e.message : "Failed"); }
    finally { setBusy(null); }
  };

  const handleCancelInvite = async (inv: FamilyInvite) => {
    setBusy(inv.id);
    try { await cancelInvite(inv.id); setPending((p) => p.filter((i) => i.id !== inv.id)); }
    catch (e: unknown) { alert(e instanceof Error ? e.message : "Failed"); }
    finally { setBusy(null); }
  };

  const handleResend = async (inv: FamilyInvite) => {
    setBusy(inv.id);
    try { await resendInvite(inv.id); }
    catch (e: unknown) { alert(e instanceof Error ? e.message : "Failed to resend"); }
    finally { setBusy(null); }
  };

  if (!homeId) {
    return (
      <div className="text-center pt-16">
        <p className="text-sm text-muted-foreground">{t('household.noHome')}</p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-8">
        {/* Section header */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium">{t('household.title')}</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              {t('household.subtitle')} <span className="text-foreground">{currentProperty?.name ?? "this home"}</span>
            </p>
          </div>
          {isOwner && (
            <Button size="sm" onClick={() => setShowInvite(true)} className="gap-1.5">
              <UserPlus className="h-3.5 w-3.5" />
              {t('household.invite')}
            </Button>
          )}
        </div>

        {error && (
          <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-xs">{error}</div>
        )}

        {/* Members list */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2].map((i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-secondary/50 animate-pulse" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-3 w-32 rounded bg-secondary/50 animate-pulse" />
                  <div className="h-2.5 w-48 rounded bg-secondary/50 animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        ) : members.length === 0 ? (
          <div className="text-center py-10 border rounded-xl border-dashed">
            <Users className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">{t('household.noMembers')}</p>
            {isOwner && (
              <button
                onClick={() => setShowInvite(true)}
                className="mt-3 text-xs underline-offset-2 underline text-muted-foreground hover:text-foreground transition-colors"
              >
                {t('household.inviteFirst')}
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y">
            {members.map((m) => (
              <MemberRow
                key={m.id}
                member={m}
                isMe={m.userId === user?.id}
                isOwner={isOwner}
                onEdit={() => setEditing(m)}
                onRemove={() => handleRemove(m)}
                onLeave={() => handleLeave(m)}
              />
            ))}
          </div>
        )}

        {/* Pending invites */}
        {isOwner && pending.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                {t('household.pendingInvitations')}
              </p>
              <span className="text-[10px] border rounded-full px-1.5 py-0.5 text-muted-foreground">
                {pending.length}
              </span>
            </div>
            <div className="divide-y rounded-xl border px-4">
              {pending.map((inv) => (
                <InviteRow
                  key={inv.id}
                  invite={inv}
                  busy={busy === inv.id}
                  onCancel={() => handleCancelInvite(inv)}
                  onResend={() => handleResend(inv)}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Invite sheet */}
      {showInvite && homeId && (
        <InviteSheet
          homeId={homeId}
          open={showInvite}
          onClose={() => setShowInvite(false)}
          onSuccess={() => { setShowInvite(false); load(); }}
        />
      )}

      {/* Edit member sheet */}
      {editing && (
        <EditMemberSheet
          member={editing}
          open={!!editing}
          onClose={() => setEditing(null)}
          onSuccess={() => { setEditing(null); load(); }}
        />
      )}
    </>
  );
}

// ─── Appearance section (self-contained so it can have local save state) ─────
function AppearanceSection({
  settings,
  updateSettings,
}: {
  settings: { colorTheme?: string; theme?: string; fontSize?: string };
  updateSettings: (s: Partial<{ colorTheme: string; theme: string; fontSize: string }>) => void;
}) {
  const { t } = useTranslation('settings');
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [saving, setSaving] = React.useState(false);
  const [dirty, setDirty] = React.useState(false);

  // Wrap updateSettings: apply locally AND persist to backend immediately
  const handleAppearanceChange = async (changes: Partial<{ colorTheme: string; theme: string; fontSize: string }>) => {
    // Update local state immediately for instant UI feedback
    updateSettings(changes);
    setDirty(true);

    // Apply to DOM immediately — don't wait for the Zustand → useEffect cycle
    const merged = { ...settings, ...changes };
    const rawTheme = merged.theme ?? "light";
    const isDark =
      rawTheme === "dark" ||
      (rawTheme === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches);
    applyDarkMode(isDark);
    applyColorPreset(merged.colorTheme ?? "301io", isDark);
    applyFontSize((merged.fontSize ?? "medium") as "small" | "medium" | "large");

  };

  const handleAppearanceSave = async () => {
    if (!dirty) return;
    setSaving(true);
    try {
      if (isAuthenticated) {
        await apiFetch(API_ENDPOINTS.USERS.UPDATE_PREFERENCES, {
          method: 'PUT',
          body: {
            colorTheme: settings.colorTheme,
            theme: settings.theme,
            fontSize: settings.fontSize,
          },
        });
        toast.success('Settings saved', {
          description: 'Your preferences have been stored in your account.',
          duration: 2000,
        });
      } else {
        toast.info('Settings saved locally', {
          description: 'Sign in to store these settings to your account.',
          duration: 2000,
        });
      }
      setDirty(false);
    } catch (error) {
      toast.error('Failed to save settings', {
        description: 'Please try again.',
        duration: 3000,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-base font-medium">{t('appearance.title')}</h2>
        <p className="text-xs text-muted-foreground mt-0.5">{t('appearance.subtitle')}</p>
      </div>

      <div className="space-y-5">

        {/* ── Brand Theme Selection ── */}
        <div className="rounded-xl border p-5 space-y-4">
          <div>
            <p className="text-sm font-medium mb-0.5">App Theme Color</p>
            <p className="text-xs text-muted-foreground">Choose your preferred brand experience</p>
          </div>

          {/* Theme Cards Grid (2 columns) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {COLOR_PRESETS.map((preset) => {
              const isSelected = (settings.colorTheme ?? "301io") === preset.id;
              const isThreeOhOne = preset.id === "301io";
              
              return (
                <button
                  key={preset.id}
                  onClick={() => handleAppearanceChange({ colorTheme: preset.id })}
                  className={cn(
                    "relative flex flex-col p-6 rounded-2xl border-2 transition-all duration-200 text-left",
                    "hover:shadow-xl hover:scale-[1.02] group",
                    isSelected
                      ? "border-current shadow-lg scale-[1.02]"
                      : "border-border hover:border-foreground/30"
                  )}
                  style={isSelected ? {
                    borderColor: `hsl(${preset.primary})`,
                    backgroundColor: `hsl(${preset.primary} / 0.02)`,
                  } : {}}
                >
                  {/* Selected Badge */}
                  {isSelected && (
                    <div 
                      className="absolute -top-3 -right-3 px-3 py-1 rounded-full text-xs font-bold text-white shadow-lg flex items-center gap-1.5 ring-2 ring-white"
                      style={{ backgroundColor: `hsl(${preset.primary})` }}
                    >
                      <Check className="h-3.5 w-3.5" />
                      ACTIVE
                    </div>
                  )}

                  {/* Logo Preview */}
                  <div className="mb-4 flex items-center justify-between">
                    {isThreeOhOne ? (
                      <span 
                        className="text-3xl font-bold tracking-tight"
                        style={{ 
                          color: `hsl(${preset.primary})`,
                          fontFamily: preset.fontFamily
                        }}
                      >
                        301io
                      </span>
                    ) : (
                      <span className="text-lg font-semibold text-foreground">
                        {preset.brandName}
                      </span>
                    )}
                    
                    {/* Status indicator */}
                    <div 
                      className={cn(
                        "w-3 h-3 rounded-full transition-all",
                        isSelected ? "ring-2 ring-offset-2" : "opacity-30"
                      )}
                      style={{ 
                        backgroundColor: `hsl(${preset.primary})`,
                        ...(isSelected ? { ringColor: `hsl(${preset.primary})` } : {})
                      }}
                    />
                  </div>

                  {/* Color Palette Preview */}
                  <div className="mb-4 flex gap-2">
                    {/* Primary Color */}
                    <div className="flex-1 space-y-1.5">
                      <div 
                        className="h-12 rounded-lg shadow-sm transition-transform group-hover:scale-105"
                        style={{ backgroundColor: `hsl(${preset.primary})` }}
                      />
                      <p className="text-[10px] text-muted-foreground font-medium text-center">Primary</p>
                    </div>
                    
                    {/* Accent Color */}
                    <div className="flex-1 space-y-1.5">
                      <div 
                        className="h-12 rounded-lg shadow-sm transition-transform group-hover:scale-105"
                        style={{ backgroundColor: `hsl(${preset.accent})` }}
                      />
                      <p className="text-[10px] text-muted-foreground font-medium text-center">Accent</p>
                    </div>
                    
                    {/* Secondary/Foreground Color */}
                    {(preset.secondary || preset.foreground) && (
                      <div className="flex-1 space-y-1.5">
                        <div 
                          className="h-12 rounded-lg shadow-sm border transition-transform group-hover:scale-105"
                          style={{ backgroundColor: `hsl(${preset.secondary || preset.foreground})` }}
                        />
                        <p className="text-[10px] text-muted-foreground font-medium text-center">Text</p>
                      </div>
                    )}
                  </div>

                  {/* Theme Info */}
                  <div className="space-y-2 mb-3">
                    <h3 
                      className="font-bold text-base tracking-tight"
                      style={isSelected ? { color: `hsl(${preset.primary})` } : {}}
                    >
                      {preset.name}
                    </h3>
                    <p className="text-xs text-muted-foreground leading-relaxed min-h-[2.5rem]">
                      {preset.description}
                    </p>
                    {preset.tagline && (
                      <div 
                        className="px-3 py-1.5 rounded-md text-[11px] italic font-medium"
                        style={{
                          backgroundColor: `hsl(${preset.primary} / 0.08)`,
                          color: `hsl(${preset.primary})`
                        }}
                      >
                        "{preset.tagline}"
                      </div>
                    )}
                  </div>

                  {/* Theme Features */}
                  <div className="pt-3 border-t border-border/50 flex flex-wrap gap-2">
                    {preset.fontFamily && (
                      <span className="px-2 py-0.5 rounded text-[10px] bg-secondary text-secondary-foreground">
                        {preset.fontFamily}
                      </span>
                    )}
                    {preset.buttonRadius === "9999px" && (
                      <span className="px-2 py-0.5 rounded text-[10px] bg-secondary text-secondary-foreground">
                        Pill Buttons
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Brightness ── */}
        <div className="rounded-xl border p-5 space-y-3">
          <div>
            <p className="text-sm font-medium mb-0.5">{t('appearance.brightness')}</p>
            <p className="text-xs text-muted-foreground">{t('appearance.brightnessDesc')}</p>
          </div>
          <div className="flex gap-2">
            {(["light", "dark", "system"] as const).map((themeOpt) => (
              <button
                key={themeOpt}
                onClick={() => handleAppearanceChange({ theme: themeOpt })}
                className={cn(
                  "flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-medium transition-all border",
                  settings.theme === themeOpt
                    ? "border-foreground bg-foreground/5 text-foreground font-semibold"
                    : "border-border bg-background text-foreground/70 hover:border-foreground/40"
                )}
              >
                {settings.theme === themeOpt && <Check className="h-3.5 w-3.5 shrink-0" />}
                {t(`appearance.${themeOpt}` as const)}
              </button>
            ))}
          </div>
        </div>

        {/* ── Font Size ── */}
        <div className="rounded-xl border p-5 space-y-3">
          <div>
            <p className="text-sm font-medium mb-0.5">{t('appearance.fontSize')}</p>
            <p className="text-xs text-muted-foreground">{t('appearance.fontSizeDesc')}</p>
          </div>
          <div className="flex gap-2">
            {(["small", "medium", "large"] as const).map((s) => (
              <button
                key={s}
                onClick={() => handleAppearanceChange({ fontSize: s })}
                className={cn(
                  "flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg transition-all border",
                  settings.fontSize === s
                    ? "border-foreground bg-foreground/5 text-foreground font-semibold"
                    : "border-border bg-background text-foreground/70 hover:border-foreground/40",
                  s === "small" ? "text-xs" : s === "large" ? "text-base" : "text-sm"
                )}
              >
                {settings.fontSize === s && <Check className="h-3.5 w-3.5 shrink-0" />}
                {t(`appearance.${s}` as const)}
              </button>
            ))}
          </div>
        </div>

        {/* ── Save Appearance ── */}
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">
            {dirty ? "Changes not saved" : t('appearance.changesPreview')}
          </p>
          <Button
            onClick={handleAppearanceSave}
            disabled={saving || !dirty}
            className="px-4"
          >
            {saving ? 'Saving...' : 'Save Theme'}
          </Button>
        </div>

      </div>
    </div>
  );
}

// ─── Main Settings page ───────────────────────────────────────────────────────
export function Settings() {
  const { t } = useTranslation('settings');
  const { settings, updateSettings } = useSettingsStore();
  const { user, clearAuth, fetchUser } = useAuthStore();
  const navigate = useNavigate();
  const [section, setSection] = useState<Section>("account");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // ── Inline name editing ──────────────────────────────────────────────────────
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [nameSaving, setNameSaving] = useState(false);
  const [nameError, setNameError] = useState('');

  const handleEditName = () => {
    setNameValue(user?.name ?? '');
    setNameError('');
    setEditingName(true);
  };

  const handleCancelName = () => {
    setEditingName(false);
    setNameError('');
  };

  const handleSaveName = async () => {
    const trimmed = nameValue.trim();
    if (!trimmed) { setNameError('Name cannot be empty'); return; }
    setNameSaving(true);
    setNameError('');
    try {
      await apiFetch(API_ENDPOINTS.USERS.UPDATE_ME, { method: 'PUT', body: { name: trimmed } });
      await fetchUser();
      setEditingName(false);
    } catch (e: unknown) {
      setNameError(e instanceof Error ? e.message : 'Failed to update name');
    } finally {
      setNameSaving(false);
    }
  };

  // ── Notification preferences (backend-synced) ──────────────────────────────
  const [prefs, setPrefs] = useState<UserPreferences | null>(null);
  const [prefsLoading, setPrefsLoading] = useState(false);
  const [prefsError, setPrefsError] = useState('');
  const [prefsSaving, setPrefsSaving] = useState<string | null>(null);

  const loadPrefs = useCallback(async () => {
    setPrefsLoading(true);
    setPrefsError('');
    try {
      const res = await apiFetch<{ data: UserPreferences }>(API_ENDPOINTS.USERS.GET_PREFERENCES);
      setPrefs(res.data);
    } catch (e: unknown) {
      setPrefsError(e instanceof Error ? e.message : 'Failed to load preferences. Please try again.');
    } finally {
      setPrefsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (section !== "notifications" && section !== "maintenance") return;
    if (prefs !== null) return; // already loaded
    loadPrefs();
  }, [section, prefs, loadPrefs]);

  const togglePref = async (key: BoolPrefKey) => {
    if (!prefs) return;
    const currentVal = prefs[key] as boolean;
    const newVal = !currentVal;
    setPrefs((p) => p ? { ...p, [key]: newVal } : p);
    setPrefsSaving(key);
    try {
      const res = await apiFetch<{ data: UserPreferences }>(
        API_ENDPOINTS.USERS.UPDATE_PREFERENCES,
        { method: 'PUT', body: { [key]: newVal } }
      );
      setPrefs(res.data);
    } catch {
      // revert on failure
      setPrefs((p) => p ? { ...p, [key]: currentVal } : p);
    } finally {
      setPrefsSaving(null);
    }
  };

  // ── Sign out all devices ────────────────────────────────────────────────────
  const [isSigningOutAll, setIsSigningOutAll] = useState(false);

  const handleSignOutAll = async () => {
    if (!window.confirm(t('security.confirmSignOut'))) return;
    setIsSigningOutAll(true);
    try {
      await apiFetch(API_ENDPOINTS.USERS.SIGNOUT_ALL, { method: 'POST' });
      clearAuth();
      navigate('/login', { replace: true });
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Failed to sign out all devices');
      setIsSigningOutAll(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') return;
    setIsDeleting(true);
    setDeleteError('');
    try {
      await apiFetch(API_ENDPOINTS.USERS.DELETE_ME, { method: 'DELETE' });
      clearAuth();
      navigate('/login', { replace: true });
    } catch (e: unknown) {
      setIsDeleting(false);
      setDeleteError(e instanceof Error ? e.message : 'Failed to delete account. Please try again.');
    }
  };

  // Allow navigating to household via URL hash
  useEffect(() => {
    const hash = window.location.hash.replace("#", "") as Section;
    if (NAV.some((n) => n.id === hash)) setSection(hash);
  }, []);

  return (
    <div>
      {/* Header */}
      <section className="border-b">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 py-6">
          <h1 className="text-4xl font-light tracking-tight">{t('title')}</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {t('subtitle')}
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 lg:px-8 py-8">
        <div className="flex gap-10">
          {/* ── Left sidebar nav ── */}
          <nav className="w-48 shrink-0 space-y-0.5">
            {NAV.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setSection(id)}
                className={cn(
                  "flex items-center gap-2.5 w-full px-3 py-2 rounded-lg text-sm transition-colors text-left",
                  section === id
                    ? "bg-secondary font-medium text-foreground"
                    : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                )}
              >
                <Icon className="h-4 w-4 shrink-0" />
                {t(`nav.${id}` as const)}
              </button>
            ))}
          </nav>

          {/* ── Content panel ── */}
          <div className="flex-1 min-w-0 max-w-2xl">

            {/* ── Account ── */}
            {section === "account" && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-base font-medium">{t('account.title')}</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">{t('account.subtitle')}</p>
                </div>
                <div className="rounded-xl border divide-y">
                  {/* Name row — inline editable */}
                  <div className="px-4 py-3.5">
                    {editingName ? (
                      <div className="space-y-2">
                        <p className="text-xs text-muted-foreground">{t('account.name')}</p>
                        <input
                          type="text"
                          value={nameValue}
                          onChange={(e) => { setNameValue(e.target.value); setNameError(''); }}
                          autoFocus
                          className="w-full h-9 px-3 text-sm rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-foreground/20 text-zinc-900 placeholder:text-muted-foreground"
                          placeholder={t('account.namePlaceholder')}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleSaveName(); if (e.key === 'Escape') handleCancelName(); }}
                        />
                        {nameError && <p className="text-xs text-destructive">{nameError}</p>}
                        <div className="flex gap-2">
                          <button
                            onClick={handleSaveName}
                            disabled={nameSaving || !nameValue.trim()}
                            className="flex items-center gap-1.5 h-8 px-3 text-xs font-medium rounded-lg bg-foreground text-background hover:opacity-90 disabled:opacity-40 transition-opacity"
                          >
                            {nameSaving ? <span className="h-3 w-3 border-2 border-background/40 border-t-background rounded-full animate-spin" /> : null}
                            {nameSaving ? t('account.saving') : t('account.save')}
                          </button>
                          <button
                            onClick={handleCancelName}
                            disabled={nameSaving}
                            className="flex items-center gap-1 h-8 px-3 text-xs rounded-lg border hover:bg-secondary/60 transition-colors disabled:opacity-40"
                          >
                            <X className="h-3 w-3" />
                            {t('account.cancel')}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-muted-foreground">{t('account.name')}</p>
                          <p className="text-sm font-medium mt-0.5">{user?.name ?? "—"}</p>
                        </div>
                        <button
                          onClick={handleEditName}
                          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                        >
                          <Pencil className="h-3 w-3" />
                          {t('account.edit')}
                        </button>
                      </div>
                    )}
                  </div>
                  {/* Email — locked */}
                  <div className="flex items-center justify-between px-4 py-3.5">
                    <div>
                      <p className="text-xs text-muted-foreground">{t('account.email')}</p>
                      <p className="text-sm font-medium mt-0.5">{user?.email ?? "—"}</p>
                    </div>
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{t('account.locked')}</span>
                  </div>
                  {/* Phone — editable via profile page */}
                  <div className="flex items-center justify-between px-4 py-3.5">
                    <div>
                      <p className="text-xs text-muted-foreground">{t('account.phone')}</p>
                      <p className="text-sm font-medium mt-0.5">{user?.phone ?? "—"}</p>
                    </div>
                    <button
                      onClick={() => navigate('/profile')}
                      className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <Pencil className="h-3 w-3" />
                      {t('account.edit')}
                    </button>
                  </div>
                </div>

                {/* Payment Methods link */}
                <button
                  type="button"
                  onClick={() => navigate('/payment-methods')}
                  className="w-full rounded-xl border bg-card px-4 py-3 flex items-center gap-3 hover:bg-secondary/40 transition-colors text-left"
                >
                  <div className="h-9 w-9 rounded-lg bg-blue-600/10 flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="14" x="2" y="5" rx="2"/><line x1="2" x2="22" y1="10" y2="10"/></svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">{t('account.paymentMethods')}</p>
                    <p className="text-xs text-muted-foreground">{t('account.paymentMethodsDesc')}</p>
                  </div>
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
                </button>

                {/* Danger Zone */}
                <div className="rounded-xl border border-red-200 dark:border-red-900 overflow-hidden">
                  <div className="bg-red-50 dark:bg-red-950/30 px-4 py-3 flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-red-600 dark:text-red-400 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>
                    <h3 className="text-sm font-semibold text-red-700 dark:text-red-400">{t('account.dangerZone')}</h3>
                  </div>
                  <div className="px-4 py-4">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-sm font-medium text-foreground">{t('account.deleteAccount')}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {t('account.deleteAccountDesc')}
                        </p>
                      </div>
                      <button
                        onClick={() => { setShowDeleteConfirm(true); setDeleteConfirmText(''); setDeleteError(''); }}
                        className="shrink-0 flex items-center gap-1.5 text-xs font-medium text-red-600 hover:text-red-700 border border-red-300 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-950/40 px-3 py-1.5 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Delete Account
                      </button>
                    </div>

                    {showDeleteConfirm && (
                      <div className="mt-4 pt-4 border-t border-red-200 dark:border-red-900 space-y-3">
                        <p className="text-sm text-foreground">
                          {t('account.deleteConfirmPrompt')}
                        </p>
                        <input
                          type="text"
                          value={deleteConfirmText}
                          onChange={(e) => setDeleteConfirmText(e.target.value)}
                          placeholder={t('account.deleteConfirmPlaceholder')}
                          className="w-full h-9 px-3 text-sm rounded-lg border border-red-300 dark:border-red-800 bg-background focus:outline-none focus:ring-2 focus:ring-red-400 placeholder:text-muted-foreground"
                          autoFocus
                        />
                        {deleteError && (
                          <p className="text-xs text-red-600">{deleteError}</p>
                        )}
                        <div className="flex gap-2">
                          <button
                            onClick={handleDeleteAccount}
                            disabled={deleteConfirmText !== 'DELETE' || isDeleting}
                            className="flex-1 h-9 text-sm font-semibold rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                          >
                            {isDeleting ? t('account.deleting') : t('account.deleteConfirm')}
                          </button>
                          <button
                            onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText(''); setDeleteError(''); }}
                            disabled={isDeleting}
                            className="flex-1 h-9 text-sm font-medium rounded-lg border border-border hover:bg-muted transition-colors"
                          >
                            {t('account.cancel')}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* ── Appearance ── */}
            {section === "appearance" && (
              <AppearanceSection settings={settings} updateSettings={updateSettings as (s: Partial<{ colorTheme: string; theme: string; fontSize: string }>) => void} />
            )}

            {/* ── Notifications ── */}
            {section === "notifications" && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-base font-medium">{t('notifications.title')}</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">{t('notifications.subtitle')}</p>
                </div>

                {/* Error state */}
                {prefsError && (
                  <div className="flex items-center gap-2.5 p-3 rounded-xl border border-destructive/20 bg-destructive/5">
                    <span className="text-destructive text-sm">{prefsError}</span>
                    <button onClick={loadPrefs} className="ml-auto text-xs underline text-destructive shrink-0">Retry</button>
                  </div>
                )}

                {/* Loading skeleton */}
                {prefsLoading ? (
                  <div className="rounded-xl border divide-y">
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                      <div key={i} className="flex items-center justify-between px-4 py-4">
                        <div className="space-y-1.5">
                          <div className="h-3 w-36 rounded bg-secondary/50 animate-pulse" />
                          <div className="h-2.5 w-52 rounded bg-secondary/50 animate-pulse" />
                        </div>
                        <div className="h-5 w-9 rounded-full bg-secondary/50 animate-pulse" />
                      </div>
                    ))}
                  </div>
                ) : !prefsError && (
                  <div className="space-y-4">
                    {/* ── Channels ── */}
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 px-1">{t('notifications.channels')}</p>
                      <div className="rounded-xl border divide-y">
                        {([
                          { key: "emailNotifications",  label: t('notifications.email'),   desc: t('notifications.emailDesc') },
                          { key: "smsNotifications",    label: t('notifications.sms'),     desc: t('notifications.smsDesc') },
                          { key: "inAppNotifications",  label: t('notifications.inApp'),   desc: t('notifications.inAppDesc') },
                        ] as { key: BoolPrefKey; label: string; desc: string }[]).map(({ key, label, desc }) => (
                          <NotifRow
                            key={key}
                            label={label}
                            desc={desc}
                            value={prefs?.[key] ?? true}
                            saving={prefsSaving === key}
                            disabled={!prefs}
                            onToggle={() => togglePref(key)}
                          />
                        ))}
                      </div>
                    </div>

                    {/* ── Types ── */}
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 px-1">{t('notifications.types')}</p>
                      <div className="rounded-xl border divide-y">
                        {([
                          { key: "maintenanceReminders",        label: t('notifications.maintenanceReminders'),    desc: t('notifications.maintenanceRemindersDesc') },
                          { key: "serviceBookingNotifications", label: t('notifications.serviceBookingUpdates'),   desc: t('notifications.serviceBookingUpdatesDesc') },
                          { key: "assetAlerts",                 label: t('notifications.assetAlerts'),             desc: t('notifications.assetAlertsDesc') },
                          { key: "marketingEmails",             label: t('notifications.marketingEmails'),         desc: t('notifications.marketingEmailsDesc') },
                        ] as { key: BoolPrefKey; label: string; desc: string }[]).map(({ key, label, desc }) => (
                          <NotifRow
                            key={key}
                            label={label}
                            desc={desc}
                            value={prefs?.[key] ?? (key !== 'marketingEmails')}
                            saving={prefsSaving === key}
                            disabled={!prefs}
                            onToggle={() => togglePref(key)}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── Maintenance ── */}
            {section === "maintenance" && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-base font-medium">{t('maintenancePrefs.title')}</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">{t('maintenancePrefs.subtitle')}</p>
                </div>

                {/* Error state */}
                {prefsError && (
                  <div className="flex items-center gap-2.5 p-3 rounded-xl border border-destructive/20 bg-destructive/5">
                    <span className="text-destructive text-sm">{prefsError}</span>
                    <button onClick={loadPrefs} className="ml-auto text-xs underline text-destructive shrink-0">Retry</button>
                  </div>
                )}

                {prefsLoading ? (
                  <div className="rounded-xl border divide-y">
                    {/* Auto-schedule skeleton */}
                    <div className="flex items-center justify-between px-4 py-4">
                      <div className="space-y-1.5">
                        <div className="h-3 w-44 rounded bg-secondary/50 animate-pulse" />
                        <div className="h-2.5 w-60 rounded bg-secondary/50 animate-pulse" />
                      </div>
                      <div className="h-5 w-9 rounded-full bg-secondary/50 animate-pulse" />
                    </div>
                    {/* Lead time skeleton */}
                    <div className="px-4 py-4 space-y-3">
                      <div className="h-3 w-32 rounded bg-secondary/50 animate-pulse" />
                      <div className="h-2.5 w-64 rounded bg-secondary/50 animate-pulse" />
                      <div className="flex gap-2">
                        {[1, 2, 3].map((i) => (
                          <div key={i} className="h-9 w-20 rounded-lg bg-secondary/50 animate-pulse" />
                        ))}
                      </div>
                    </div>
                  </div>
                ) : !prefsError && (
                  <div className="rounded-xl border divide-y">

                    {/* Auto-Schedule toggle — uses same NotifRow for consistency */}
                    <NotifRow
                      label={t('maintenancePrefs.autoSchedule')}
                      desc={t('maintenancePrefs.autoScheduleDesc')}
                      value={prefs?.autoScheduleMaintenance ?? true}
                      saving={prefsSaving === 'autoScheduleMaintenance'}
                      disabled={!prefs}
                      onToggle={() => togglePref('autoScheduleMaintenance')}
                    />

                    {/* Reminder Lead Time */}
                    <div className="px-4 py-4">
                      <p className="text-sm font-medium mb-1">{t('maintenancePrefs.leadTime')}</p>
                      <p className="text-xs text-muted-foreground mb-3">{t('maintenancePrefs.leadTimeDesc')}</p>
                      <div className="flex gap-2">
                        {([1, 3, 7] as const).map((days) => {
                          const selected = (prefs?.advanceNoticeDays ?? 7) === days;
                          const isSavingThis = prefsSaving === 'advanceNoticeDays';
                          return (
                            <button
                              key={days}
                              disabled={isSavingThis || !prefs}
                              onClick={async () => {
                                if (!prefs || prefsSaving) return;
                                const prev = prefs.advanceNoticeDays;
                                setPrefs((p) => p ? { ...p, advanceNoticeDays: days } : p);
                                setPrefsSaving('advanceNoticeDays');
                                try {
                                  const res = await apiFetch<{ data: UserPreferences }>(
                                    API_ENDPOINTS.USERS.UPDATE_PREFERENCES,
                                    { method: 'PUT', body: { advanceNoticeDays: days } }
                                  );
                                  setPrefs(res.data);
                                } catch {
                                  setPrefs((p) => p ? { ...p, advanceNoticeDays: prev } : p);
                                } finally {
                                  setPrefsSaving(null);
                                }
                              }}
                              className={cn(
                                "flex-1 py-2.5 rounded-lg text-sm font-medium transition-all border disabled:opacity-40",
                                selected
                                  ? "border-foreground bg-foreground/5 text-foreground font-semibold"
                                  : "border-border bg-background text-foreground/70 hover:border-foreground/40"
                              )}
                            >
                              {days === 1 ? t('maintenancePrefs.oneDay') : t('maintenancePrefs.nDays', { n: days })}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                  </div>
                )}
              </div>
            )}

            {/* ── Security ── */}
            {section === "security" && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-base font-medium">{t('security.title')}</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">{t('security.subtitle')}</p>
                </div>
                <div className="rounded-xl border divide-y">
                  {/* 2FA — coming soon */}
                  <div className="flex items-center justify-between px-4 py-4">
                    <div>
                      <p className="text-sm font-medium">{t('security.twoFA')}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{t('security.twoFADesc')}</p>
                    </div>
                    <span className="text-[10px] px-2 py-0.5 rounded-full border border-border text-muted-foreground uppercase tracking-wider">
                      {t('security.comingSoon')}
                    </span>
                  </div>
                  <div className="flex items-start justify-between px-4 py-4 gap-4">
                    <div>
                      <p className="text-sm font-medium">{t('security.signOutAll')}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {t('security.signOutAllDesc')}
                      </p>
                    </div>
                    <button
                      onClick={handleSignOutAll}
                      disabled={isSigningOutAll}
                      className="shrink-0 text-xs font-medium border border-border hover:bg-secondary/60 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {isSigningOutAll ? t('security.signingOut') : t('security.signOutButton')}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ── Language ── */}
            {section === "language" && <LanguageSection />}

            {/* ── Household ── */}
            {section === "household" && <HouseholdSection />}
          </div>
        </div>
      </section>
    </div>
  );
}
